import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FiActivity,
  FiUser,
  FiFlag,
  FiCheckSquare,
  FiClock,
  FiFileText,
  FiPaperclip,
  FiEdit2,
  FiTrash2,
  FiCornerDownRight,
  FiCalendar,
  FiEye,
  FiPlusSquare,
  FiAlignLeft,
  FiType,
  FiLayers,
  FiRefreshCw,
  FiArchive,
  FiRotateCcw,
} from "react-icons/fi";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { getTaskActivity } from "../../../services/taskActivity.service";

dayjs.extend(relativeTime);

const UI = {
  bg: "#FFFFFF",
  surface: "#F8FAFC",
  border: "#CBD5E1",
  borderLight: "#E2E8F0",
  text: "#0F172A",
  textSub: "#334155",
  textMuted: "#64748B",
  accent: "#0D9488",
  accentSoft: "#CCFBF1",
  danger: "#DC2626",
  warning: "#D97706",
  success: "#16A34A",
  info: "#6366F1",
};

const ACTION_META = {
  TASK_CREATED:                  { icon: FiPlusSquare,      color: UI.success, label: "created this task" },
  ASSIGNEE_CHANGED:              { icon: FiUser,            color: UI.info,    label: "changed the assignee" },
  SUPPORT_CHANGED:               { icon: FiUser,            color: UI.info,    label: "changed the support person" },
  ACKNOWLEDGED_BY_CHANGED:       { icon: FiUser,            color: UI.info,    label: "changed the acknowledged by person" },
  STATUS_CHANGED:                { icon: FiCheckSquare,     color: UI.accent,  label: "changed the status" },
  PRIORITY_CHANGED:              { icon: FiFlag,            color: UI.warning, label: "changed the priority" },
  ISSUE_TYPE_CHANGED:            { icon: FiLayers,          color: UI.info,    label: "changed the issue type" },
  TITLE_CHANGED:                 { icon: FiType,            color: UI.info,    label: "updated the title" },
  DESCRIPTION_UPDATED:           { icon: FiAlignLeft,       color: UI.info,    label: "updated the description" },
  DEADLINE_CHANGED:              { icon: FiCalendar,        color: UI.warning, label: "updated the deadline" },
  PARENT_TASK_CHANGED:           { icon: FiCornerDownRight, color: UI.info,    label: "updated the parent task" },
  FILE_ADDED:                    { icon: FiPaperclip,       color: UI.success, label: "added file(s)" },
  FILE_REMOVED:                  { icon: FiPaperclip,       color: UI.danger,  label: "removed file(s)" },
  WORKLOG_ADDED:                 { icon: FiClock,           color: UI.accent,  label: "logged work" },
  WORKLOG_UPDATED:               { icon: FiClock,           color: UI.warning, label: "updated a work log" },
  WORKLOG_REMOVED:               { icon: FiClock,           color: UI.danger,  label: "removed a work log" },
  VERIFY_STATUS_CHANGED:         { icon: FiCheckSquare,     color: UI.info,    label: "changed verify status" },
  CLIENT_VERIFY_STATUS_CHANGED:  { icon: FiCheckSquare,     color: UI.info,    label: "changed client verify status" },
  CLIENT_VERIFIED:               { icon: FiCheckSquare,     color: UI.success, label: "marked as client-verified" },
  CLIENT_VIEW_TOGGLED:           { icon: FiEye,             color: UI.info,    label: "toggled client view" },
  RECURRING_ENABLED:             { icon: FiRefreshCw,       color: UI.accent,  label: "enabled recurring" },
  RECURRING_DISABLED:            { icon: FiRefreshCw,       color: UI.danger,  label: "disabled recurring" },
  RECURRING_DATES_CHANGED:       { icon: FiRefreshCw,       color: UI.warning, label: "updated recurring period" },
  CRON_TASK_CREATED:             { icon: FiRefreshCw,       color: UI.accent,  label: "spawned a recurring task" },
  ARCHIVED:                      { icon: FiArchive,         color: UI.warning, label: "archived this task" },
  RESTORED:                      { icon: FiRotateCcw,       color: UI.success, label: "restored this task from archive" },
  DELETED:                       { icon: FiTrash2,          color: UI.danger,  label: "deleted this task" },
  UPDATED:                       { icon: FiEdit2,           color: UI.info,    label: "updated this task" },
  CREATED:                       { icon: FiPlusSquare,      color: UI.success, label: "created this task" },
  DRAGGED:                       { icon: FiCheckSquare,     color: UI.accent,  label: "moved this task" },
};

const fallbackMeta = { icon: FiActivity, color: UI.textMuted, label: "made a change" };

// ── Filter group definitions ──────────────────────────────────────────────────
const FILTER_GROUPS = [
  { key: "status",      label: "Status",      icon: FiCheckSquare,     actions: ["STATUS_CHANGED", "DRAGGED"] },
  { key: "title",       label: "Title",       icon: FiType,            actions: ["TITLE_CHANGED"] },
  { key: "description", label: "Description", icon: FiAlignLeft,       actions: ["DESCRIPTION_UPDATED"] },
  { key: "priority",    label: "Priority",    icon: FiFlag,            actions: ["PRIORITY_CHANGED"] },
  { key: "assignee",    label: "Assignee",    icon: FiUser,            actions: ["ASSIGNEE_CHANGED"] },
  { key: "support",     label: "Support",     icon: FiUser,            actions: ["SUPPORT_CHANGED"] },
  { key: "acknowledgedBy", label: "Acknowledged By", icon: FiUser, actions: ["ACKNOWLEDGED_BY_CHANGED"] },
  { key: "deadline",    label: "Deadline",    icon: FiCalendar,        actions: ["DEADLINE_CHANGED"] },
  { key: "worklog",     label: "Work Log",    icon: FiClock,           actions: ["WORKLOG_ADDED", "WORKLOG_UPDATED", "WORKLOG_REMOVED"] },
  { key: "files",       label: "Files",       icon: FiPaperclip,       actions: ["FILE_ADDED", "FILE_REMOVED"] },
  { key: "type",        label: "Issue Type",  icon: FiLayers,          actions: ["ISSUE_TYPE_CHANGED"] },
  { key: "verify",      label: "Verify",      icon: FiCheckSquare,     actions: ["VERIFY_STATUS_CHANGED", "CLIENT_VERIFY_STATUS_CHANGED", "CLIENT_VERIFIED"] },
  { key: "parent",      label: "Parent",      icon: FiCornerDownRight, actions: ["PARENT_TASK_CHANGED"] },
  { key: "created",     label: "Created",     icon: FiPlusSquare,      actions: ["TASK_CREATED", "CREATED", "CRON_TASK_CREATED"] },
  { key: "recurring",   label: "Recurring",   icon: FiRefreshCw,       actions: ["RECURRING_ENABLED", "RECURRING_DISABLED", "RECURRING_DATES_CHANGED", "CRON_TASK_CREATED", "TASK_CREATED", "CREATED"], metaFilter: null },
  { key: "archive",     label: "Archive",     icon: FiArchive,         actions: ["ARCHIVED", "RESTORED"] },
  { key: "other",       label: "Other",       icon: FiActivity,        actions: ["CLIENT_VIEW_TOGGLED", "UPDATED", "DELETED"] },
];

// ── Value formatter ───────────────────────────────────────────────────────────
const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(v)) return dayjs(v).format("D MMM YYYY");
    return v;
  }
  return String(v);
};

const ChangeChip = ({ children, tone = "default" }) => {
  const palette = {
    default: { bg: UI.borderLight, fg: UI.text },
    old:     { bg: "#FEE2E2",      fg: "#991B1B" },
    new:     { bg: "#DCFCE7",      fg: "#166534" },
  };
  const p = palette[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.8, py: 0.1,
        fontSize: "11.5px", fontWeight: 600,
        borderRadius: "4px",
        background: p.bg, color: p.fg,
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  );
};

const ActivityBody = ({ item }) => {
  const before = item.oldLabel ?? item.oldValue;
  const after  = item.newLabel ?? item.newValue;

  switch (item.action) {
    case "TASK_CREATED":
    case "CREATED": {
      const who = item.meta?.assigneeName;
      const isRecurring = item.meta?.isRecurring;
      const recurStart = item.meta?.recurringStartDate;
      const recurEnd   = item.meta?.recurringEndDate;
      const isCron     = item.meta?.isFromTemplate === true;
      return (
        <Box sx={{ mt: 0.4 }}>
          {/* Task number + assignee row */}
          <Stack direction="row" alignItems="center" spacing={0.6} flexWrap="wrap">
            {item.meta?.taskNo && (
              <Box
                sx={{
                  px: "7px", py: "2px", borderRadius: "5px",
                  bgcolor: `${UI.success}14`,
                  border: `1px solid ${UI.success}30`,
                }}
              >
                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: UI.success }}>
                  {item.meta.taskNo}
                </Typography>
              </Box>
            )}
            {who && (
              <Typography sx={{ fontSize: "12.5px", color: UI.textSub }}>
                assigned to <strong>{who}</strong>
              </Typography>
            )}
          </Stack>

          {/* Created via Cron / Manual badge */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.8}
            sx={{
              mt: 0.8,
              px: 1.2, py: 0.5,
              borderRadius: "8px",
              bgcolor: isCron ? "#F0FDFA" : "#F8FAFC",
              border: `1px solid ${isCron ? "#99D6D1" : UI.borderLight}`,
              display: "inline-flex",
            }}
          >
            {isCron
              ? <FiRefreshCw size={11} color={UI.accent} />
              : <FiPlusSquare size={11} color={UI.textMuted} />
            }
            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: isCron ? UI.accent : UI.textMuted }}>
              {isCron ? "Created via Cron" : "Created Manually"}
            </Typography>
          </Stack>

          {/* Recurring badge — only shown when isRecurring is true */}
          {isRecurring && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.8}
              sx={{
                mt: 0.6,
                px: 1.2, py: 0.6,
                borderRadius: "8px",
                bgcolor: "#F0FDFA",
                border: "1px solid #99D6D1",
                display: "inline-flex",
              }}
            >
              <FiRefreshCw size={11} color={UI.accent} />
              <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: UI.accent }}>
                Recurring Task
              </Typography>
              {recurStart && recurEnd && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#99D6D1" }} />
                  <Typography sx={{ fontSize: "11.5px", color: "#0F766E", fontWeight: 500 }}>
                    {dayjs(recurStart).format("DD MMM YYYY")}
                    <Box component="span" sx={{ mx: 0.5, color: "#99D6D1" }}>→</Box>
                    {dayjs(recurEnd).format("DD MMM YYYY")}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Box>
      );
    }
    case "RECURRING_ENABLED": {
      const start = item.meta?.startDate;
      const end   = item.meta?.endDate;
      const fmtRange = (s, e) => {
        if (!s || !e) return null;
        const sd = dayjs(s), ed = dayjs(e);
        return sd.year() === ed.year()
          ? `${sd.format("DD MMM")} – ${ed.format("DD MMM YYYY")}`
          : `${sd.format("DD MMM YYYY")} – ${ed.format("DD MMM YYYY")}`;
      };
      const rangeLabel = fmtRange(start, end);
      return (
        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.4, flexWrap: "wrap" }}>
          <ChangeChip tone="old">Disabled</ChangeChip>
          <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
          <ChangeChip tone="new">Enabled</ChangeChip>
          {rangeLabel && (
            <>
              <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>·</Typography>
              <ChangeChip tone="new">
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  <FiRefreshCw size={9} />
                  {rangeLabel}
                </Box>
              </ChangeChip>
            </>
          )}
        </Stack>
      );
    }
    case "RECURRING_DISABLED":
      return (
        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.5 }}>
          <ChangeChip tone="old">Enabled</ChangeChip>
          <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
          <ChangeChip tone="new">Disabled</ChangeChip>
        </Stack>
      );
    case "RECURRING_DATES_CHANGED": {
      const os = item.meta?.oldStartDate;
      const oe = item.meta?.oldEndDate;
      const ns = item.meta?.startDate;
      const ne = item.meta?.endDate;
      const fmtRange = (s, e) => {
        if (!s && !e) return "—";
        const sd = s ? dayjs(s) : null;
        const ed = e ? dayjs(e) : null;
        if (sd && ed) {
          return sd.year() === ed.year()
            ? `${sd.format("DD MMM")} – ${ed.format("DD MMM YYYY")}`
            : `${sd.format("DD MMM YYYY")} – ${ed.format("DD MMM YYYY")}`;
        }
        return sd ? sd.format("DD MMM YYYY") : ed.format("DD MMM YYYY");
      };
      return (
        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.4, flexWrap: "wrap" }}>
          <ChangeChip tone="old">
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <FiRefreshCw size={9} />
              {fmtRange(os, oe)}
            </Box>
          </ChangeChip>
          <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
          <ChangeChip tone="new">
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <FiRefreshCw size={9} />
              {fmtRange(ns, ne)}
            </Box>
          </ChangeChip>
        </Stack>
      );
    }
    case "CRON_TASK_CREATED": {
      const isSource = !!item.meta?.spawnedTask;   // entry on the original recurring task
      const isSpawn  = !!item.meta?.sourceTask;    // entry on the newly spawned task
      const month    = item.meta?.month;
      const year     = item.meta?.year;
      const monthName = month && year
        ? new Date(year, month - 1, 1).toLocaleString("default", { month: "long" })
        : null;
      return (
        <Box sx={{ mt: 0.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.8}
            flexWrap="wrap"
            sx={{
              px: 1.2, py: 0.6, borderRadius: "8px",
              bgcolor: "#F0FDFA", border: "1px solid #99D6D1",
              display: "inline-flex",
            }}
          >
            <FiRefreshCw size={11} color={UI.accent} />
            <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: UI.accent }}>
              {isSource ? "Spawned new task" : "Created by recurring cron"}
            </Typography>
            {monthName && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#99D6D1" }} />
                <Typography sx={{ fontSize: "11.5px", color: "#0F766E", fontWeight: 500 }}>
                  {monthName} {year}
                </Typography>
              </>
            )}
          </Stack>
          {isSpawn && item.meta?.sourceTitle && (
            <Typography sx={{ fontSize: "12px", color: UI.textSub, mt: 0.5 }}>
              from recurring task: <strong>{item.meta.sourceTitle}</strong>
            </Typography>
          )}
          {/* v2 templates spawn from a recurringTemplate document, not from a
              source task, so neither isSource nor isSpawn is set for them. */}
          {!isSource && !isSpawn && item.meta?.templateTitle && (
            <Typography sx={{ fontSize: "12px", color: UI.textSub, mt: 0.5 }}>
              from recurring template: <strong>{item.meta.templateTitle}</strong>
              {item.meta.templateNo ? ` (${item.meta.templateNo})` : ""}
            </Typography>
          )}
        </Box>
      );
    }
    case "STATUS_CHANGED":
      return (
        <Box>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.4, flexWrap: "wrap" }}>
            {before !== undefined && <ChangeChip tone="old">{formatValue(before)}</ChangeChip>}
            {before !== undefined && after !== undefined && (
              <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
            )}
            {after !== undefined && <ChangeChip tone="new">{formatValue(after)}</ChangeChip>}
          </Stack>
          {item.meta?.note && (
            <Box sx={{ mt: 0.8, px: 1.2, py: 0.8, borderRadius: "8px", bgcolor: "#FFF7ED", border: "1px solid #FED7AA" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#C2410C", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>
                Rework note
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#92400E", lineHeight: 1.5 }}>
                {item.meta.note}
              </Typography>
            </Box>
          )}
        </Box>
      );
    case "DEADLINE_CHANGED":
      return (
        <Box>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.4, flexWrap: "wrap" }}>
            {before !== undefined && <ChangeChip tone="old">{formatValue(before)}</ChangeChip>}
            {before !== undefined && after !== undefined && (
              <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
            )}
            {after !== undefined && <ChangeChip tone="new">{formatValue(after)}</ChangeChip>}
          </Stack>
          {item.meta?.reason && (
            <Box sx={{ mt: 0.8, px: 1.2, py: 0.8, borderRadius: "8px", bgcolor: "#FFFBEB", border: "1px solid #FDE68A" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.3 }}>
                Reason for change
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#92400E", lineHeight: 1.5 }}>
                {item.meta.reason}
              </Typography>
            </Box>
          )}
        </Box>
      );
    case "PRIORITY_CHANGED":
    case "ASSIGNEE_CHANGED":
    case "SUPPORT_CHANGED":
    case "ACKNOWLEDGED_BY_CHANGED":
    case "ISSUE_TYPE_CHANGED":
    case "VERIFY_STATUS_CHANGED":
    case "CLIENT_VERIFY_STATUS_CHANGED":
    case "CLIENT_VERIFIED":
    case "CLIENT_VIEW_TOGGLED":
      return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.4, flexWrap: "wrap" }}>
          {before !== undefined && <ChangeChip tone="old">{formatValue(before)}</ChangeChip>}
          {before !== undefined && after !== undefined && (
            <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
          )}
          {after !== undefined && <ChangeChip tone="new">{formatValue(after)}</ChangeChip>}
        </Stack>
      );
    case "TITLE_CHANGED":
      return (
        <Typography sx={{ fontSize: "12.5px", color: UI.textSub, mt: 0.3, fontStyle: "italic" }}>
          {after ? `"${after}"` : null}
        </Typography>
      );
    case "DESCRIPTION_UPDATED":
      return (
        <Box
          sx={{
            mt: 0.5, px: 1.2, py: 0.6, borderRadius: "6px",
            background: UI.accentSoft,
            border: `1px solid ${UI.accent}30`,
            display: "inline-flex", alignItems: "center", gap: "6px",
          }}
        >
          <FiFileText size={11} color={UI.accent} />
          <Typography sx={{ fontSize: "12px", color: UI.accent, fontWeight: 600 }}>
            Description content was modified
          </Typography>
        </Box>
      );
    case "FILE_ADDED":
    case "FILE_REMOVED":
      return (
        <Typography sx={{ fontSize: "12.5px", color: UI.textSub, mt: 0.3 }}>
          {item.meta?.count ?? "1"} file{(item.meta?.count ?? 1) > 1 ? "s" : ""}
          {item.meta?.files?.length
            ? `: ${item.meta.files.slice(0, 3).join(", ")}${item.meta.files.length > 3 ? "…" : ""}`
            : ""}
        </Typography>
      );
    case "ARCHIVED":
    case "RESTORED":
      // Lifecycle rows carry no old/new value — the default branch would render
      // nothing, so spell out what actually happened to the task.
      return (
        <Typography sx={{ fontSize: "12.5px", color: UI.textSub, mt: 0.3 }}>
          {item.action === "ARCHIVED"
            ? "Moved to Archived — removed from the board and list views"
            : "Moved back to the board from Archived"}
          {item.meta?.taskNo ? ` · ${item.meta.taskNo}` : ""}
        </Typography>
      );
    case "WORKLOG_ADDED":
    case "WORKLOG_UPDATED":
    case "WORKLOG_REMOVED": {
      const e = item.action === "WORKLOG_UPDATED" ? item.meta?.after : item.meta;
      if (!e?.startTime) return null;
      return (
        <Typography sx={{ fontSize: "12.5px", color: UI.textSub, mt: 0.3 }}>
          {dayjs(e.date || e.startTime).format("DD MMM YYYY")} ·{" "}
          {dayjs(e.startTime).format("hh:mm A")} – {dayjs(e.endTime).format("hh:mm A")}
        </Typography>
      );
    }
    default:
      if (before !== undefined || after !== undefined) {
        return (
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.4 }}>
            {before !== undefined && <ChangeChip tone="old">{formatValue(before)}</ChangeChip>}
            {before !== undefined && after !== undefined && (
              <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>→</Typography>
            )}
            {after !== undefined && <ChangeChip tone="new">{formatValue(after)}</ChangeChip>}
          </Stack>
        );
      }
      return null;
  }
};

// ── Main component ────────────────────────────────────────────────────────────
const Activity = ({ taskId, isFromTemplate }) => {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [pagination, setPagination] = useState({});
  const [activeFilter, setActiveFilter] = useState(null); // null = "All"

  const load = useCallback(async (pg = 1, append = false) => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await getTaskActivity(taskId, { page: pg, limit: 50 });
      if (res?.error === false) {
        setItems((prev) => (append ? [...prev, ...res.data] : res.data));
        setPagination(res.pagination || {});
        setPage(pg);
      }
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(1, false); }, [load]);

  // Compute which filter groups actually have data
  const availableFilters = useMemo(() => {
    const presentActions = new Set(items.map((i) => i.action));
    return FILTER_GROUPS.filter((g) => {
      const hasAction = g.actions.some((a) => presentActions.has(a));
      if (!hasAction) return false;
      if (g.metaFilter) return items.some((i) => g.actions.includes(i.action) && g.metaFilter(i));
      return true;
    });
  }, [items]);

  // Apply active filter
  const filteredItems = useMemo(() => {
    if (!activeFilter) return items;
    const group = FILTER_GROUPS.find((g) => g.key === activeFilter);
    if (!group) return items;
    return items.filter((i) => {
      if (!group.actions.includes(i.action)) return false;
      if (group.metaFilter) return group.metaFilter(i);
      return true; // metaFilter is null — all matching actions pass
    });
  }, [items, activeFilter]);

  if (loading && items.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={20} sx={{ color: UI.accent }} />
      </Stack>
    );
  }

  if (items.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: "50%", background: UI.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", color: UI.accent }}>
          <FiActivity size={20} />
        </Box>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 600 }}>No activity yet</Typography>
        <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>Changes to this task will appear here.</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ border: `1px solid ${UI.border}`, borderRadius: "12px", overflow: "hidden", background: UI.bg }}>

      {/* ── Filter bar ── */}
      <Box
        sx={{
          px: 1.5, py: 1.2,
          borderBottom: `1px solid ${UI.borderLight}`,
          background: UI.surface,
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: "3px" },
          "&::-webkit-scrollbar-thumb": { background: UI.border, borderRadius: "2px" },
        }}
      >
        <Stack direction="row" spacing={0.7} sx={{ width: "max-content" }}>
          {/* All chip */}
          <Box
            onClick={() => setActiveFilter(null)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              px: "10px", py: "5px", borderRadius: "20px", cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
              background: activeFilter === null ? UI.accent : "transparent",
              border: `1.5px solid ${activeFilter === null ? UI.accent : UI.borderLight}`,
              transition: "all 0.15s",
              "&:hover": activeFilter !== null ? { border: `1.5px solid ${UI.border}`, background: "#F1F5F9" } : {},
            }}
          >
            <FiActivity size={11} color={activeFilter === null ? "#fff" : UI.textMuted} />
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: activeFilter === null ? "#fff" : UI.textMuted }}>
              All
            </Typography>
            <Box
              sx={{
                minWidth: 18, height: 18, px: "5px", borderRadius: "9px",
                background: activeFilter === null ? "rgba(255,255,255,0.25)" : UI.borderLight,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: activeFilter === null ? "#fff" : UI.textMuted, lineHeight: 1 }}>
                {items.length}
              </Typography>
            </Box>
          </Box>

          {/* Dynamic filter chips */}
          {availableFilters.map((group) => {
            const isActive = activeFilter === group.key;
            const Icon = group.icon;
            const count = items.filter((i) => group.actions.includes(i.action) && (!group.metaFilter || group.metaFilter(i))).length;
            return (
              <Box
                key={group.key}
                onClick={() => setActiveFilter(isActive ? null : group.key)}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "5px",
                  px: "10px", py: "5px", borderRadius: "20px", cursor: "pointer",
                  background: isActive ? UI.accent : "transparent",
                  border: `1.5px solid ${isActive ? UI.accent : UI.borderLight}`,
                  transition: "all 0.15s",
                  "&:hover": !isActive ? { border: `1.5px solid ${UI.border}`, background: "#F1F5F9" } : {},
                }}
              >
                <Icon size={11} color={isActive ? "#fff" : UI.textMuted} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isActive ? "#fff" : UI.textMuted, whiteSpace: "nowrap" }}>
                  {group.label}
                </Typography>
                <Box
                  sx={{
                    minWidth: 18, height: 18, px: "5px", borderRadius: "9px",
                    background: isActive ? "rgba(255,255,255,0.25)" : UI.borderLight,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: isActive ? "#fff" : UI.textMuted, lineHeight: 1 }}>
                    {count}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* ── Activity feed ── */}
      <Box sx={{ maxHeight: "min(440px, 55vh)", overflowY: "auto", p: 2 }}>
        {/* Creation source banner */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.8}
          sx={{
            mb: 2,
            px: 1.4, py: 0.7,
            borderRadius: "8px",
            bgcolor: isFromTemplate ? "#F0FDFA" : "#F8FAFC",
            border: `1px solid ${isFromTemplate ? "#99D6D1" : UI.borderLight}`,
            display: "inline-flex",
          }}
        >
          {isFromTemplate
            ? <FiRefreshCw size={12} color={UI.accent} />
            : <FiPlusSquare size={12} color={UI.textMuted} />
          }
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: isFromTemplate ? UI.accent : UI.textMuted }}>
            {isFromTemplate ? "Created via Cron" : "Created Manually"}
          </Typography>
        </Stack>
        {filteredItems.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.textMuted }}>No matching activity</Typography>
            <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>
              Try selecting a different filter.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {filteredItems.map((item, idx) => {
              const meta = ACTION_META[item.action] || fallbackMeta;
              const Icon = meta.icon;
              const isLast = idx === filteredItems.length - 1;

              return (
                <Stack key={item._id} direction="row" spacing={1.5} alignItems="flex-start">
                  {/* Timeline gutter */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.2 }}>
                    <Box
                      sx={{
                        width: 28, height: 28, borderRadius: "50%",
                        background: `${meta.color}1A`, color: meta.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1px solid ${meta.color}33`,
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={13} />
                    </Box>
                    {!isLast && (
                      <Box sx={{ width: "2px", flex: 1, background: UI.borderLight, mt: 0.6, minHeight: 24 }} />
                    )}
                  </Box>

                  {/* Body */}
                  <Box sx={{ flex: 1, pb: isLast ? 0 : 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Avatar
                        src={item.actorAvatar || item.assigned?.file || undefined}
                        sx={{ width: 22, height: 22, fontSize: "10px", fontWeight: 700, bgcolor: UI.accent, color: "#fff" }}
                      >
                        {(item.actorName || item.assigned?.name || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: UI.text }}>
                        {item.actorName || item.assigned?.name || "Unknown user"}
                      </Typography>
                      <Typography sx={{ fontSize: "12.5px", color: UI.textSub }}>
                        {meta.label}
                      </Typography>
                      <Tooltip title={dayjs(item.createdAt || item.date).format("DD MMM YYYY, hh:mm A")}>
                        <Typography sx={{ fontSize: "11.5px", color: UI.textMuted }}>
                          · {dayjs(item.createdAt || item.date).fromNow()}
                        </Typography>
                      </Tooltip>
                    </Stack>
                    <ActivityBody item={item} />
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}

        {pagination?.hasMore && !activeFilter && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Button
              onClick={() => load(page + 1, true)}
              disabled={loading}
              sx={{
                textTransform: "none", color: UI.accent, fontWeight: 600, fontSize: "12.5px",
                "&:hover": { background: UI.accentSoft },
              }}
            >
              {loading ? "Loading…" : `Load more (${pagination.total - items.length} remaining)`}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default Activity;
